/* eslint-disable max-lines */
/* eslint-disable import/max-dependencies */

import React, { useEffect, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useTranslate } from '@im/react-localization';
import AddVpnPage from './AddVpnPage';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { DataGrid } from 'src/components/DataGrid';
import { GridColDef, GridSelectionModel } from '@mui/x-data-grid';
import { LineDetails } from '../../hooks/deal-details';
import { useSearchParams } from 'react-router-dom';
import { StyledText } from 'src/components/StyledTypography';
import {
  mergedUniqueList,
  useAddVpnUpdate,
  useVpnListQuery,
  VpnAddQueryRequest,
  VpnListQueryRequest,
  Product,
} from '../../hooks/vpn-list';
import { useRoles } from 'src/provider/UserRoleProvider';

interface VpnListPageProps {
  handleClose?: () => void;
  importreferenceid: number;
  lineDetails: LineDetails;
}
export default function VpnListPage(props: Readonly<VpnListPageProps>) {
  const translate = useTranslate();
  const [searchParams] = useSearchParams();
  const vendorProgramId = searchParams.get('vendorProgramId');
  const { isSalesUser } = useRoles();
  const [selectionMode, setSelectionMode] = useState<GridSelectionModel>([]);
  const [selectRowformesg, setselectRowformesg] = useState<number>();
  const [lineDetailsForVpnList, setlineDetailsForVpnList] =
    useState<boolean>(false);
  const [mode, setMode] = useState<string>(
    props.lineDetails.is_product_member_exclude === 'True' ? 'Yes' : 'No'
  );
  const requestBody: VpnListQueryRequest = {
    VpnId: props.lineDetails.sbo2_product_set_Id,
  };
  const [rows, setRows] = useState<Product[]>([]);
  const { data, isLoading } = useVpnListQuery(requestBody, true);
  useEffect(() => {
    const initialrows =
      data?.productMembers.map((item, index) => ({
        Id: index,
        productMember: item,
      })) ?? [];
    setRows(initialrows);
  }, [data?.productMembers]);
  const [memberType, setMemberType] = useState<string>('VPN');
  useEffect(() => {
    if (data?.productMemberType) {
      setMemberType(data.productMemberType);
    }
  }, [data?.productMemberType]);
  const requestBodyAddvpn: VpnAddQueryRequest = {
    dealId: String(props.lineDetails.deal_number),
    discountDetailId: props.lineDetails.discount_detail_id,
    isProductMemberExclude: mode,
    productMemberType: data?.productMemberType ?? '',
    productMembers: rows
      .filter((row) => !selectionMode.includes(row.Id))
      .map((row) => row.productMember),
    sbo2ProductSetId: props.lineDetails.sbo2_product_set_Id,
    vendorProgramId: Number(vendorProgramId),
  };
  const VpnAddDetail = useAddVpnUpdate(requestBodyAddvpn);

  const addVpnList = (newItem: Product[]) => {
    const niqueList = mergedUniqueList(rows, newItem);
    setRows(niqueList);
  };

  const handleModeChange = (event: SelectChangeEvent) => {
    setMode(event.target.value);
  };
  const [reprocessMsg, setReprocessMsg] = useState<string | undefined>();
  const handleRemoveSelected = () => {
    const newRows = rows.filter((row) => !selectionMode.includes(row.Id));
    setselectRowformesg(selectionMode.length);
    setRows(newRows);
    setSelectionMode([]);
    setReprocessMsg(undefined);
    VpnAddDetail.mutateAsync()
      .then((response) => setReprocessMsg('Success'))
      .catch((error) => setReprocessMsg(error));
  };
  const upDateSetReprocessMsg = () => {
    setReprocessMsg(undefined);
    setselectRowformesg(0);
  };
  const columns: GridColDef<typeof rows[number]>[] = [
    {
      align: 'left',
      cellClassName: 'cellvender',
      field: 'productMember',
      headerName: 'PRODUCTS',
      renderCell: (item) => {
        const value = <StyledText noWrap>{item.value}</StyledText>;
        return <Tooltip title={value}>{value}</Tooltip>;
      },
      sortable: false,
      width: 200,
    },
  ];
  const handleToggle = () => {
    setlineDetailsForVpnList(!lineDetailsForVpnList);
  };
  return (
    <Drawer
      anchor={'right'}
      data-testid="gds-vp-VpnListpage"
      onClose={props.handleClose}
      open={Boolean(props.lineDetails)}
      sx={{
        '.MuiDrawer-paperAnchorRight': {
          height: `calc(100vh - 50px)`,
          top: 50,
          width: '30%',
        },
      }}
    >
      <Stack
        data-testid="vp-VpnListpage-title-header"
        gap={2}
        paddingTop={2}
        paddingX={2}
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography
            data-testid="gds-vp-VPN-List-table heading"
            fontSize={16}
            lineHeight={`22px`}
            sx={{ alignContent: 'center', display: 'grid' }}
            variant="smalltextsemibold"
          >
            {translate('VendorProgram.VendorVpn', {
              id: props.lineDetails.vendor_number,
            })}
          </Typography>
          <IconButton
            data-testid="gds-vp-vpnlist-icon-btn"
            onClick={props.handleClose}
            size="small"
            sx={{ alignSelf: 'baseline' }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider />
        <Stack flexDirection={'row'} justifyContent={'space-between'}>
          <Select
            data-testid="gds-vp-vpn-list-select-change"
            disabled={rows.length > 0}
            onChange={handleModeChange}
            size="small"
            sx={{
              '&.multiSelect-icon': {
                border: '1px',
                color: 'blue',
              },
              color: '#0073E5',
              height: '30px',
              lineHeight: '30px',
            }}
            value={mode}
            variant={'outlined'}
          >
            <MenuItem data-testid="gds-vp-vpn-list-select-1" value="No">
              {translate('VendorProgram.Include')}
            </MenuItem>
            <MenuItem data-testid="gds-vp-vpn-list-select-2" value="Yes">
              {translate('VendorProgram.Exclude')}
            </MenuItem>
          </Select>
          <Select
            data-testid="gds-vp-vpn-sku-list-select-change"
            disabled={rows.length > 0}
            onChange={(event) => setMemberType(event.target.value)}
            size="small"
            sx={{
              '&.multiSelect-icon': {
                border: '1px',
                color: 'blue',
              },
              color: '#0073E5',
              height: '30px',
              lineHeight: '30px',
            }}
            value={memberType}
            variant={'outlined'}
          >
            <MenuItem data-testid="gds-vp-vpn-sku-list-select-1" value="VPN">
              {translate('VendorProgram.VPNProductMemberType')}
            </MenuItem>
            <MenuItem data-testid="gds-vp-vpn-sku-list-select-2" value="SKU">
              {translate('VendorProgram.SKUProductMemberType')}
            </MenuItem>
          </Select>
          <Button
            data-testid="gds-vp-vpn-list-addpropd"
            disabled={isSalesUser}
            onClick={handleToggle}
            startIcon={<AddIcon />}
            variant={'newContained'}
          >
            {translate('VendorProgram.AddProduct')}
          </Button>
        </Stack>
      </Stack>
      {lineDetailsForVpnList && (
        <AddVpnPage
          handleClose={() => setlineDetailsForVpnList(false)}
          lineDetails={props.lineDetails}
          lineDetailsvpn={lineDetailsForVpnList}
          onAddvpnlist={addVpnList}
          productMemberType={memberType}
          reprocessMsgprops={upDateSetReprocessMsg}
          selectedMode={mode}
          vpnlistRows={rows}
        />
      )}
      {reprocessMsg === 'Success' ? (
        <Box
          alignItems={'center'}
          display={'flex'}
          flexDirection={'column'}
          height={'50vh'}
          justifyContent={'center'}
        >
          <CheckCircleIcon sx={{ color: '#1976d2', fontSize: 64 }} />
          <Typography variant="h1">
            {selectRowformesg}{' '}
            {translate('VendorProgram.VpnProductRemoved', {
              mode: mode === 'Yes' ? 'Exclusion' : 'Inclusion',
            })}
          </Typography>
        </Box>
      ) : (
        <>
          <Box overflow="auto" p={2}>
            <DataGrid
              autoHeight
              checkboxSelection
              columnBuffer={columns.length}
              columns={columns}
              data-testid="gds-vp-Vpn-list-dataGrid"
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              disableSelectionOnClick
              getRowHeight={() => 'auto'}
              getRowId={(id) => id.Id}
              hideFooterPagination={true}
              loading={isLoading}
              onSelectionModelChange={(newSelection) => {
                setSelectionMode(newSelection);
              }}
              rows={rows as any}
              sx={{
                '.MuiDataGrid-columnHeader': {
                  padding: '0 5px',
                },
                '.MuiDataGrid-columnHeader--moving': {
                  backgroundColor: 'transparent',
                },
                '.MuiDataGrid-columnHeaderTitle': {
                  whiteSpace: 'normal',
                },
                '.MuiDataGrid-row': {
                  minHeight: '52px !important',
                },
              }}
            />
          </Box>
          <Stack
            direction="row"
            justifyContent={'flex-end'}
            marginTop="auto"
            paddingX={`16px`}
            paddingY={`24px`}
            spacing={`12px`}
          >
            <Button
              data-testid="gds-vp-vpnlist-cancel-btn"
              onClick={props.handleClose}
              variant="newOutlined"
            >
              {translate('Common.Cancel')}
            </Button>
            <Button
              data-testid="gds-vp-vp-vpnlisty-btn"
              disabled={selectionMode.length === 0 || isSalesUser}
              onClick={handleRemoveSelected}
              variant="newContained"
            >
              {translate('VendorDeals.RemoveSelectedRow')}
            </Button>
          </Stack>
        </>
      )}{' '}
    </Drawer>
  );
}
